import {
  action,
  computed,
  type IReactionDisposer,
  makeObservable,
  observable,
  reaction,
} from 'mobx'
import dayjs from 'dayjs'
import { logger } from 'shared/lib'
import type {
  IParamsTestBroadcast,
  IResponseAssignee,
  IResponseOneTimeBroadcast,
  IResponseOneTimeBroadcastStatus,
} from 'entities/Broadcast/api/types'
import type { IBroadcastType } from 'entities/Broadcast'
import { User } from 'entities/Users/model/User'
import { BroadcastOneTimeStatistic } from 'entities/Broadcast/model/BroadcastOneTimeStatistic'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { type IResponseMediaShort } from 'entities/Attachment'
import { getSendFromInfo, type ISendFromInfo } from 'entities/SendFrom'
import { Phone } from 'entities/Phone/model/Phone'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { BROADCAST_REFRESH_DELAY_IN_PROGRESS_INTERVAL } from 'pages/broadcasts/constants'

export class BroadcastOneTime {
  id: number
  recurring_parent_id?: number | null
  type!: IBroadcastType
  name!: string
  owner_id!: number
  status!: IResponseOneTimeBroadcastStatus
  failed_reason!: string | null
  broadcastOwner?: User
  created_at!: string

  sent_at!: string
  send_at!: string
  is_track_conversion?: boolean
  conversion_id?: number | null = null

  _statistics: BroadcastOneTimeStatistic | null = null

  sendFromNumbersProps: ISendFromInfo | null = null

  message = ''
  media_url: IResponseMediaShort[] = []
  attachments: Attachment[] = []
  contacts_count: number | null = null
  invalid_contacts_count: number | null = null
  short_urls: IResponseOneTimeBroadcast['short_urls'] | null = []
  package_count = 0
  sendFrom: IResponseOneTimeBroadcast['send_from'] | null = null
  assignee: IResponseAssignee | null = null
  number: IResponseOneTimeBroadcast['number'] | null = null
  response: IResponseOneTimeBroadcast
  testNumberId = 0

  constructor(item: IResponseOneTimeBroadcast, private _entities: IEntitiesStore) {
    this.id = item.id
    this.response = item
    this.syncOrigin(item)

    makeObservable(this, {
      status: observable,
      syncOrigin: action,
      _statistics: observable,
      statistics: computed,
      sent_at: observable,
      send_at: observable,
    })
  }

  syncOrigin = (item: IResponseOneTimeBroadcast) => {
    this.id = item.id
    this.recurring_parent_id = item.recurring_parent_id
    this._statistics = new BroadcastOneTimeStatistic(item, this._entities)
    this.type =
      item.send_from?.type === 'from_rcs_sender' ? 'rcs' : item.is_rvm ? 'ringless' : 'sms'
    this.status = item.status
    this.failed_reason = item.failed_reason
    this.sent_at = item.sent_at
    this.send_at = item.send_at || ''

    this.broadcastOwner = item.user_owner ? new User(item.user_owner) : undefined
    this.is_track_conversion = item.is_track_conversion
    this.conversion_id = item.conversion_id
    this.sendFromNumbersProps = this.getSendFromNumbersProps(item)
    this.testNumberId = this.getTestNumberId(item)
    this.owner_id = item.owner_id
    this.name = item.name
    this.created_at = item.created_at
    this.message = item.message || ''
    this.media_url = item.media_url ?? []
    this.attachments = Array.isArray(item.media_url)
      ? item.media_url.map((media) => new Attachment({ responseMediaShort: media }))
      : []
    this.contacts_count = item.contacts_count
    this.invalid_contacts_count = item.invalid_contacts_count
    this.short_urls = item.short_urls
    this.package_count = item.package_count
    this.sendFrom = item.send_from
    this.number = item.number
    this.assignee = item.assignee
  }
  init = () => {
    this._reactionStatus()
    return this.dispose
  }
  dispose = () => {
    if (this._statusTimeoutId) clearTimeout(this._statusTimeoutId)
    this._disposeStatus?.()
  }

  get user() {
    const userFormStore = this._entities.usersStore.getItem(this.owner_id)
    if (userFormStore) {
      return userFormStore
    }
    if (this.broadcastOwner) {
      return this.broadcastOwner
    } else {
      return this._entities.usersStore.getItem(this.owner_id, true)
    }
  }

  getTestNumberId = (broadcast: IResponseOneTimeBroadcast): number => {
    if (broadcast.team) return broadcast.team.numbers[0]?.id ?? 0
    return broadcast.number?.id ?? 0
  }

  getSendFromNumbersProps = (broadcast: IResponseOneTimeBroadcast) => {
    if (!broadcast.send_from) return null
    return getSendFromInfo(broadcast.send_from, {
      number: broadcast.number ? new Phone(broadcast.number, this._entities) : undefined,
      team: broadcast.team ? new Inbox(broadcast.team, this._entities) : undefined,
    })
  }

  get statistics() {
    return this._statistics
  }

  get inboxId() {
    if (this.sendFrom?.type === 'from_contact_owner') {
      return null
    }

    if (this.sendFrom?.options.team_id) {
      const inbox = this._entities.inboxesStore.getItem(this.sendFrom?.options.team_id)

      return inbox?.id
    }

    if (this.sendFrom?.options.number_id) {
      const inbox = this._entities.inboxesStore.getInboxByNumber(this.sendFrom?.options.number_id)

      return inbox?.id
    }

    if (this.number?.id) {
      const inbox = this._entities.inboxesStore.getInboxByNumber(this.number.id)

      return inbox?.id
    }
  }

  get isInvalid() {
    return this.status === 'invalid'
  }

  get testSMSParams() {
    const params: IParamsTestBroadcast = {
      media_url: this.media_url,
      message: this.message,
    }

    if (this.testNumberId) params.number_id = this.testNumberId
    if (this.type === 'rcs' && this.inboxId) params.team_id = this.inboxId

    return params
  }

  private _statusTimeoutId: NodeJS.Timeout | null = null
  private _handleStatus = () => {
    if (this.status === 'in_progress' || this.status === 'scheduled') {
      if (this._statusTimeoutId) clearTimeout(this._statusTimeoutId)
      this._statusTimeoutId = setTimeout(async () => {
        try {
          if (this.status === 'scheduled' && dayjs(this.send_at).isAfter(dayjs())) {
            this._handleStatus()
          } else {
            const data = await this._entities.broadcastStore.getDelayItemById(this.id)
            if (data.status !== 'recurring_template') {
              this.syncOrigin(data)
              this._handleStatus()
            }
          }
        } catch (e) {
          logger.error(e)
        }
      }, BROADCAST_REFRESH_DELAY_IN_PROGRESS_INTERVAL)
    } else {
      if (this._statusTimeoutId) clearTimeout(this._statusTimeoutId)
    }
  }
  private _disposeStatus: IReactionDisposer | null = null
  private _reactionStatus = () => {
    this._disposeStatus?.()
    this._disposeStatus = reaction(() => this.status, this._handleStatus, {
      fireImmediately: true,
    })
  }
}
